import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, delay, filter, map, switchMap } from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'

import { selectIncidentsRedesignEnabled } from 'containers/App/selectors'
import { selectDeepfakeVisibility } from 'containers/Navigation/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { REGULAR_FILTERS_CHANGE } from 'containers/ResolutionCenter/IncidentFilters/constants'
import {
  selectDefaultIncidentDateFilterWithDefault,
  selectEffectiveIncidentDateFilter,
  selectRegularFilters,
} from 'containers/ResolutionCenter/IncidentFilters/selectors'
import { selectUrlState } from 'containers/ResolutionCenter/List/selectors'

import {
  incidentsCountPolling,
  incidentsCountPollingError,
  incidentsCountPollingSuccess,
} from './actions'
import {
  DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC,
  DEEPFAKE_POLLING_INTERVAL,
  INCIDENTS_COUNT_POLLING_ASYNC,
  MINIMAL_ALLOWED_POLLING_INTERVAL,
} from './constants'
import { resolveIncidentCountDateFilter } from './resolveIncidentCountDateFilter'

/**
 * An example of setInterval on the Epic level.
 * Main benefit of this is that every new request
 * happens only once previous is finished with
 * an ability to define a custom delay.
 */
const incidentsCountPollingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- feeds the top-bar incidents badge (selectIncidentsCount), which is visible on every route, so this poller must keep refreshing on a company-filter change regardless of page
    ofType(
      INCIDENTS_COUNT_POLLING_ASYNC.INITIATE,
      INCIDENTS_COUNT_POLLING_ASYNC.SUCCESS,
      INCIDENTS_COUNT_POLLING_ASYNC.ERROR,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      REGULAR_FILTERS_CHANGE
    ),
    filter((action) =>
      action.type === REGULAR_FILTERS_CHANGE &&
      (action.payload.filterType === 'dateFilter' ||
        action.payload.filterType === 'customDateRange')
        ? true
        : action.type !== INCIDENTS_COUNT_POLLING_ASYNC.ERROR && action.error?.status !== 403
    ),
    switchMap((action) => of(action).pipe(delay(MINIMAL_ALLOWED_POLLING_INTERVAL))),
    switchMap(() => {
      if (document.visibilityState === 'hidden') {
        return of(incidentsCountPolling())
      } else {
        const state = state$.value
        const selectedCompany = selectCompanyFilter(state)?.value ?? ''
        let dateFilter
        let feDateRange = null
        try {
          const regularFilters = selectRegularFilters(state)
          // Same dateFilter as the incidents list POST: legacy Challenged is
          // hardcoded Last 24 hours (3); redesign follows the picker.
          dateFilter = resolveIncidentCountDateFilter({
            urlState: selectUrlState(state),
            incidentsRedesignEnabled: selectIncidentsRedesignEnabled(state),
            dateFilter: regularFilters.dateFilter,
            effectiveDateFilter: selectEffectiveIncidentDateFilter(state),
          })
          // Redesign Custom stores ALL_TIMES (7) plus customDateRange; send the
          // range the same way the incidents list POST does so badges match rows.
          feDateRange = regularFilters.customDateRange || null
        } catch (e) {
          try {
            dateFilter = selectEffectiveIncidentDateFilter(state)
          } catch (err) {
            try {
              dateFilter = selectDefaultIncidentDateFilterWithDefault(state)
            } catch (fallbackErr) {
              dateFilter = null
            }
          }
        }
        return api.resolutionCenter
          .fetchIncidentsCount(selectedCompany, dateFilter, 'open', feDateRange)
          .pipe(
            map((response) => incidentsCountPollingSuccess(response)),
            catchError((error) => of(epicsErrorHandler(incidentsCountPollingError, error, [403])))
          )
      }
    })
  )

/**
 * Deepfake notifications polling epic with exponential backoff.
 * Polls the deepfake alerts endpoint with increasing delays on errors.
 * Resets delay on successful requests. Continues polling indefinitely.
 */
const deepfakeNotificationsPollingEpic = (action$, state$, { api, epicsErrorHandler }) => {
  let retryCount = 0
  const baseDelay = DEEPFAKE_POLLING_INTERVAL
  const maxDelay = 30000 // 30 seconds max delay

  const getDelay = () => {
    if (retryCount === 0) return baseDelay
    return Math.min(baseDelay * 2 ** (retryCount - 1), maxDelay)
  }

  return action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- getDeepfakeAlerts() takes no company argument (the backend scopes it to the session), and the alert badge polls on every route
    ofType(
      DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC.INITIATE,
      DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC.SUCCESS,
      DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC.ERROR,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter(() => selectDeepfakeVisibility(state$.value)),
    switchMap((action) => of(action).pipe(delay(getDelay()))),
    switchMap(() =>
      api.companies.getDeepfakeAlerts().pipe(
        map((response) => {
          // Reset retry count on success
          retryCount = 0
          return DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC.onSuccess({ data: response })
        }),
        catchError((error) => {
          // Increment retry count on error
          retryCount += 1

          return of(epicsErrorHandler(DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC.onError, error, [403]))
        })
      )
    )
  )
}

// Both leaves are SELF-DRIVING: each only re-runs when it sees its own
// SUCCESS/ERROR action (see the `.ofType(...)` lists above). After a crash,
// `isolateOne`'s resubscribe re-establishes the subscription but emits no
// action of its own, so a self-driving loop has nothing to restart it and
// would stay dead for the rest of the session. Each leaf therefore declares
// its OWN re-kick. This is per-leaf rather than per-key on purpose: a single
// key-level kick would also restart the healthy sibling and fire a
// duplicate request — reads may crash independently of one another.
//
// A re-kick against a still-broken read crashes again immediately, so five
// kicks exhaust the 60s retry budget in ~5s and terminate in
// [root-epic-give-up]. That is the intended outcome — a fast, fingerprinted
// death beats an invisible flap — so triage such an alert as "the read is
// broken", not "the re-kick is broken".
export default combineEpicsIsolated(
  { epic: incidentsCountPollingEpic, rekick: () => incidentsCountPolling() },
  {
    epic: deepfakeNotificationsPollingEpic,
    rekick: () => DEEPFAKE_NOTIFICATIONS_POLLING_ASYNC.initiate(),
  }
)
